//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, AlertLevelEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { IPoolBaseAbi, PoolDynamicAbi } from '@shared/Abis'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { otAssert } from '@shared/conversions'

import { alertActions } from '@react/redux/alertSlice'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { PoolDynamic } from '@typechain-types/index'
import { UserConnection } from '@shared/DeploymentConfigTypes'

export const dynamicOffChainDepositTx =
  (props: {
    poolAddr: EthAddr

    lenderAddr: EthAddr
    assets: string | bigint | number
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolAddr, lenderAddr, assets } = props
    const activity = ActivityEnum.POOL_ACTIVATE
    try {
      logOT.debug(activity)

      const { webSocket } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      const { roleEnum, signer } = getState().chainState.userConnection as UserConnection
      assertRole(roleEnum, RoleEnum.POOL_ADMIN)
      otAssert(signer, 'signer is not defined')

      await dynamicOffChainDeposit(
        webSocket,
        signer as Signer,
        poolAddr,
        lenderAddr,
        BigInt(assets),
        chainConfigurationName
      )

      dispatch(poolActions.setOffchainDepositSuccess({ poolAddr }))
    } catch (e: any) {
      dispatch(poolActions.setOffchainDepositFail({}))
      dispatch(
        alertActions.addAlert({
          title: 'Offchain Deposit failed.',
          description: 'Please wait and try again',
          createdAt: Date.now() / 1000,
          dueDateTime: Date.now() / 1000,
          txHash: '',
          sourceObjectAddr: ETH_ADDR_ZERO,
          alertLevel: AlertLevelEnum.ERROR,
        })
      )
      await dispatchError(dispatch, e, activity, 1037)
    }
  }
export const dynamicOffChainDeposit = async (
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolAddr: EthAddr,
  lenderAddr: EthAddr,
  assets: bigint,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('activatePool ')
  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.POOL_DEPOSIT, poolAddr, uuid, chainConfigurationName))

  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, poolAdminSigner)
  const poolType = Number(await poolBaseContract.poolType())

  if (poolType === PoolTypeEnum.Dynamic) {
    const poolContract = new Contract(poolAddr, PoolDynamicAbi, poolAdminSigner) as unknown as PoolDynamic
    const tx = await poolContract.depositOffChain(assets, lenderAddr)

    webSocket?.send(
      wsServerActionPoolTX(ActivityEnum.POOL_DEPOSIT, poolAddr, uuid, tx.hash as TxHash, chainConfigurationName)
    )

    await tx.wait(1)
  } else {
    throw new Error('Unknown pool type')
  }
}
